import { Typography } from '@recruit-robin/robin-theme/web-components';
import { Trash_01 } from '@recruit-robin/robin-theme/web-icons';
import capitalize from 'lodash/capitalize';
import { memo, useCallback, useContext, useMemo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import type { Vacancy } from 'clients/AutoCompleteClient/types';
import type { VacanciesListProps } from './VacancyList.types';

import { useDialog } from 'shared/legacy/contexts/DialogContext/hooks/useDialog';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { NoVacanciesFound } from './components/NoVacanciesFound/NoVacanciesFound';
import { VacancyListItem } from './components/VacancyListItem/VacancyListItem';
import { useHeightFromParentsMaxHeight } from './hooks/useHeightFromParentsMaxHeight';
import { useStyles } from './VacancyList.styles';

export const VacancyList = ({
  selectedVacancy,
  onCreateJob,
  onSelectVacancy,
  onDeleteVacancy,
  onEditVacancy,
  vacanciesList,
  showEnrichedVacancyButton,
  disableTooltip = false,
}: VacanciesListProps) => {
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);

  const defaultDialog = useDialog();
  const { setContainerRef } = useHeightFromParentsMaxHeight({ defaultHeight: vacanciesList.length * 36 });

  const handleSelect = useCallback(
    (vacancy: Vacancy) => {
      onSelectVacancy(vacancy);
    },
    [onSelectVacancy],
  );

  const handleDelete = useCallback(
    (vacancy: Vacancy) => {
      defaultDialog.open({
        actionType: 'negative',
        description: (
          <div css={classes.dialogBody}>
            <Trash_01 size={24} color={(c) => c.tertiary.red[50]} />
            <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
              {capitalize(dictionary.removeEnrichedVersion)}?
            </Typography>
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
              {dictionary.removeEnrichedVersionDescription}
            </Typography>
          </div>
        ),
        confirmAction: {
          label: dictionary.yesRemoveIt,
          onClick: () => onDeleteVacancy?.(vacancy),
        },
        cancelAction: { label: dictionary.close },
      });
    },
    [defaultDialog, classes.dialogBody, dictionary, onDeleteVacancy],
  );

  // Memoized item data for virtualization to prevent unnecessary re-renders
  const itemData = useMemo(
    () => ({
      vacanciesList,
      selectedVacancy,
      showEnrichedVacancyButton,
      onSelect: handleSelect,
      onDelete: onDeleteVacancy,
      onEdit: onEditVacancy,
      disableTooltip,
      handleDelete,
    }),
    [
      vacanciesList,
      selectedVacancy,
      showEnrichedVacancyButton,
      handleSelect,
      onDeleteVacancy,
      onEditVacancy,
      disableTooltip,
      handleDelete,
    ],
  );

  if (vacanciesList.length === 0) {
    return <NoVacanciesFound onCreateJob={onCreateJob} />;
  }

  return (
    <div ref={setContainerRef} css={classes.vacanciesListContainer} className="esse">
      <AutoSizer>
        {({ height, width }) => (
          <FixedSizeList
            height={height}
            width={width}
            itemCount={vacanciesList.length}
            itemSize={36}
            itemData={itemData}
            overscanCount={15}
            itemKey={(index, data) => data.vacanciesList[index].value}>
            {VacancyListRow}
          </FixedSizeList>
        )}
      </AutoSizer>
    </div>
  );
};

const VacancyListRow = memo<
  ListChildComponentProps<{
    vacanciesList: Vacancy[];
    selectedVacancy: Vacancy | null;
    showEnrichedVacancyButton?: boolean;
    onSelect: (vacancy: Vacancy) => void;
    onDelete?: (vacancy: Vacancy) => void;
    onEdit?: (vacancy: Vacancy) => void;
    disableTooltip?: boolean;
    handleDelete: (vacancy: Vacancy) => void;
  }>
>(({ data, index, style }) => {
  const {
    vacanciesList,
    selectedVacancy,
    showEnrichedVacancyButton,
    onSelect,
    onDelete,
    onEdit,
    disableTooltip,
    handleDelete,
  } = data;

  const vacancy = vacanciesList[index];

  return (
    <div style={style}>
      <VacancyListItem
        vacancy={vacancy}
        showEnrichedVacancyButton={showEnrichedVacancyButton}
        isSelected={
          vacancy.is_improved && selectedVacancy?.db_id
            ? vacancy.db_id === selectedVacancy?.db_id
            : vacancy.value === selectedVacancy?.value
        }
        onSelect={onSelect}
        onDelete={onDelete ? handleDelete : undefined}
        onEdit={onEdit}
        disableTooltip={disableTooltip ?? false}
      />
    </div>
  );
});

VacancyListRow.displayName = 'VacancyListRow';
